import React, { useState } from "react";
import { PageShell } from "../components/Layout";
import api from "../lib/api";
import { Sparkle, ArrowRight, ShieldWarning, ShieldCheck, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import KundaliChart from "../components/KundaliChart";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import PaywallGate from "../components/PaywallGate";

const SEVERITY_TINT = {
  high: { bg: "from-rose-50 to-orange-50", border: "border-rose-300", badge: "bg-rose-200 text-rose-900", icon: "text-rose-600", label: "HIGH" },
  moderate: { bg: "from-amber-50 to-yellow-50", border: "border-amber-300", badge: "bg-amber-200 text-amber-900", icon: "text-amber-600", label: "MODERATE" },
  info: { bg: "from-indigo-50 to-violet-50", border: "border-indigo-200", badge: "bg-indigo-200 text-indigo-900", icon: "text-indigo-600", label: "AWARENESS" },
};

export default function DoshasPage() {
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  const [placeQuery, setPlaceQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  usePrefillBirthDetails(setForm, setPlaceQuery);

  const submit = async (e) => {
    e?.preventDefault();
    const place = form.place || placeQuery;
    if (!form.date || !form.time || !place) {
      toast.error("Please fill date, time and place");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/doshas/compute", { ...form, place });
      setData(r.data);
      setTimeout(() => document.querySelector("[data-testid='doshas-result']")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to compute doshas");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20">
        <header className="mb-10">
          <div className="label-eyebrow text-muted flex items-center gap-2">
            <ShieldWarning size={14} weight="duotone" /> Doshas & Remedies · D1 + D9 analysis
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
            Doshas in your <span className="text-gold">birth chart</span>
          </h1>
          <p className="text-muted text-sm sm:text-base mt-4 font-light max-w-2xl">
            Detects classical Vedic doshas in your D1 (Rasi) and D9 (Navamsa) charts — Mangal, Pitru, Putra, Shrapit, Vish, Guru-Chandal, Kemadruma, Kaal-Sarpa, Grahan, Gandanta, Sankranti and Maraka-sthana awareness — with chart-specific reasoning and remedy playbook for each.
          </p>
        </header>

        <form onSubmit={submit} className="rounded-2xl p-6 sm:p-7 bg-[#FAF4E8] border border-soft space-y-5 max-w-2xl" data-testid="doshas-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="space-y-1">
              <span className="text-xs text-muted">Date of birth</span>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="doshas-date" required />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted">Time of birth</span>
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="doshas-time" required />
            </label>
          </div>
          <div className="relative">
            <label className="space-y-1 block">
              <span className="text-xs text-muted">Place of birth</span>
              <input
                type="text" value={placeQuery}
                onChange={async (e) => {
                  const q = e.target.value;
                  setPlaceQuery(q);
                  if (q.trim().length >= 2) {
                    try {
                      const { data: sug } = await api.get("/places/search", { params: { q: q.trim() } });
                      setSuggestions(sug || []);
                    } catch { /* ignore */ }
                  } else setSuggestions([]);
                }}
                placeholder="Start typing — Mumbai, Delhi, London..."
                className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                data-testid="doshas-place"
              />
            </label>
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg">
                {suggestions.map((s, i) => (
                  <button type="button" key={i}
                    onClick={() => {
                      const label = s.name || s.label;
                      setForm({ ...form, place: label, timezone: s.timezone || "Asia/Kolkata" });
                      setPlaceQuery(label); setSuggestions([]);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b border-soft last:border-0"
                    data-testid={`doshas-place-option-${i}`}
                  >{s.name || s.label}</button>
                ))}
              </div>
            )}
          </div>
          <button type="submit" disabled={loading} className="btn-primary rounded-full px-7 py-3 font-medium disabled:opacity-50" data-testid="doshas-submit">
            {loading ? "Scanning your charts…" : <>Reveal my doshas <ArrowRight size={16} className="inline ml-1" /></>}
          </button>
        </form>

        {data && (
          <div className="mt-12 space-y-10" data-testid="doshas-result">
            {/* === D1 + D9 CHARTS === */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="rounded-2xl p-5 bg-gradient-to-br from-[#FFF8E7] to-[#FFE7C2] border border-amber-200" data-testid="d1-chart-card">
                <div className="label-eyebrow text-[#7C2D12]">D1 · Rasi (Lagna {data.d1_lagna_rashi})</div>
                <div className="mt-4 flex justify-center">
                  <KundaliChart kundali={data.d1_chart} size={300} />
                </div>
                <p className="text-xs text-muted mt-3 text-center font-light">Your foundational birth chart — physical body, day-to-day karma, persona.</p>
              </div>
              <div className="rounded-2xl p-5 bg-gradient-to-br from-[#F0F4FF] to-[#E0EAFF] border border-indigo-200" data-testid="d9-chart-card">
                <div className="label-eyebrow text-indigo-900">D9 · Navamsa (Lagna {data.d9_lagna_rashi})</div>
                <div className="mt-4 flex justify-center">
                  <KundaliChart kundali={data.d9_chart} size={300} />
                </div>
                <p className="text-xs text-muted mt-3 text-center font-light">Your soul-level chart — marriage karma, inner strength, post-30 manifestation.</p>
              </div>
            </section>

            {/* === SUMMARY BAND === */}
            <section className="rounded-2xl p-6 bg-[#FAF4E8] border border-soft" data-testid="dosha-summary">
              <div className="flex items-baseline justify-between flex-wrap gap-3">
                <div className="label-eyebrow text-muted">Detected doshas</div>
                <div className="text-sm text-muted">
                  <span className="text-rose-900 font-medium">{data.summary.high_count} high</span> ·{" "}
                  <span className="text-amber-900 font-medium">{data.summary.moderate_count} moderate</span> ·{" "}
                  <span className="text-indigo-900 font-medium">{data.summary.info_count} awareness</span>
                </div>
              </div>
              {data.doshas.length === 0 && (
                <div className="mt-6 text-center text-emerald-900 py-8">
                  <ShieldCheck size={32} weight="duotone" className="mx-auto text-emerald-600" />
                  <p className="font-serif-display text-2xl mt-2 font-light">No major doshas detected</p>
                  <p className="text-sm text-muted mt-1">Your chart is karmically clean across the 11 classical screening tests.</p>
                </div>
              )}
            </section>

            {/* === DOSHA CARDS === */}
            {data.doshas.length > 0 && (
              <PaywallGate
                tool="doshas"
                toolLabel="Doshas & Remedies"
                birthDetails={{ date: form.date, time: form.time, place: form.place || placeQuery, timezone: form.timezone }}
                report={data}
              >
                <section className="space-y-5">
                  {data.doshas.map((dh, i) => {
                    const tint = SEVERITY_TINT[dh.severity] || SEVERITY_TINT.moderate;
                    const Icon = dh.severity === "info" ? Info : dh.severity === "high" ? ShieldWarning : Sparkle;
                    return (
                      <article key={i} className={`rounded-2xl overflow-hidden border ${tint.border}`} data-testid={`dosha-card-${dh.key}`}>
                        <div className={`p-6 bg-gradient-to-br ${tint.bg}`}>
                          <div className="flex items-baseline gap-3 flex-wrap">
                            <Icon size={18} weight="duotone" className={tint.icon} />
                            <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full ${tint.badge}`}>{tint.label}</span>
                            <h3 className="font-serif-display text-2xl font-light text-[#3F2D1B]">{dh.name}</h3>
                          </div>
                          <p className="text-xs text-[#3F2D1B]/80 mt-3 italic"><b>Why this fires for you:</b> {dh.why}</p>
                          {dh.cancellation && dh.cancellation.length > 0 && (
                            <p className="text-xs text-emerald-900 mt-2"><b>Cancellation factors:</b> {dh.cancellation.join(" · ")}</p>
                          )}
                          <p className="text-sm mt-4 font-light leading-snug text-[#3F2D1B]">{dh.meaning}</p>
                        </div>
                        {dh.remedies && dh.remedies.length > 0 && (
                          <div className="p-6 bg-white">
                            <div className="label-eyebrow text-muted mb-3">Remedy playbook</div>
                            <ul className="space-y-2 text-sm">
                              {dh.remedies.map((r, j) => (
                                <li key={j} className="flex gap-3 leading-snug">
                                  <span className="text-gold mt-0.5">✦</span>
                                  <span className="text-[#3F2D1B]">{r}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </article>
                    );
                  })}
                </section>
              </PaywallGate>
            )}

            <ToolAstrologerCta
              requiredTechnique="Vedic"
              pitch="Get a live walk-through of your dosha-remedies + custom puja calendar — with a Vedic specialist"
              ctaLabel="Talk to"
            />
          </div>
        )}
      </div>
    </PageShell>
  );
}
